import React, { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, Autocomplete, TextField, Checkbox, TableFooter, IconButton,
  Box
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import styled from 'styled-components';

import OffReadOnlyRow from './OffReadOnlyRow';
import OffEditableRow from './OffEditableRow';
import AddOffice from './AddOffice';
import ExcelDownload from '../../../data/exceldownload';
import nameStringFn from '../../../data/nameStringFn';

import {
  useDeleteofficeMutation,
  useGetofficesQuery,
  useUpdateofficeMutation
} from '../../../features/offices/officesApiSlice';
import {
  useGetofficetypeQuery,
} from '../../../features/officetype/officetypeApiSlice';
import { useGetdesignationsQuery } from '../../../features/designations/designationApiSlice';
import { useGetUsersQuery } from '../../../features/users/usersApiSlice';

import { HeadingWrap, Span, CommonButton } from '../../../Styles/CommonStyles';
import { TableContStyle, TableHeadStyle } from '../../../Styles/TableStyles';


const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const tableFontSize = 14; // Base font size

// Styled Components
const Displaydiv1 = styled.div`
  padding: 12px;
  
  @media (min-width: 1920px) {
    padding: 16px;
  }
  
  @media (max-width: 1440px) {
    padding: 8px;
  }
  
  @media (max-width: 1200px) {
    padding: 4px;
  }
`;

const ResponsiveHeadingWrap = styled(HeadingWrap)`
  margin-bottom: 16px;
  
  @media (min-width: 1920px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 1440px) {
    margin-bottom: 12px;
  }
  
  @media (max-width: 1200px) {
    margin-bottom: 8px;
  }
`;

const ResponsiveSpan = styled(Span)`
  @media (min-width: 1920px) {
    font-size: 28px;
  }
  
  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: 24px;
  }
  
  @media (min-width: 1200px) and (max-width: 1440px) {
    font-size: 24px;
  }
  
  @media (max-width: 1200px) {
    font-size: 18px;
  }
`;

const AddOfficeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;
  gap: 12px;
  
  @media (min-width: 1920px) {
    margin-bottom: 24px;
    gap: 16px;
  }
  
  @media (max-width: 1440px) {
    gap: 8px;
  }
`;

const AddOffSubWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 12px;
  
  @media (min-width: 1920px) {
    gap: 16px;
  }
`;

const ResponsiveCommonButton = styled(CommonButton)`
  @media (min-width: 1920px) {
    font-size: 14px;
    padding: 10px 20px;
  }
  
  @media (max-width: 1440px) {
    font-size: 12px;
    padding: 6px 12px;
  }
  
  @media (max-width: 1200px) {
    font-size: 11px;
    padding: 4px 8px;
  }
`;

const SubmitButton = styled(CommonButton)`
  height: 40px;
  margin-top: 0px;
  margin-bottom: 0px;
`;

const ResponsiveTableContainer = styled(TableContainer)`
  max-width: 100vw;
  overflow-x: auto;
  
  @media (min-width: 1920px) {
    font-size: 16px;
  }
  
  @media (max-width: 1440px) {
    font-size: 12px;
  }
  
  @media (max-width: 1200px) {
    font-size: 11px;
  }
`;

const CompactTableCell = styled(TableCell)`
  padding: 10px 16px !important;
  font-size: ${tableFontSize}px !important;
  
  @media (min-width: 1920px) {
    padding: 12px 20px !important;
    font-size: 16px !important;
  }
  
  @media (min-width: 1600px) and (max-width: 1919px) {
    padding: 10px 16px !important;
    font-size: 15px !important;
  }
  
  @media (max-width: 1440px) {
    padding: 6px 8px !important;
    font-size: 12px !important;
  }
  
  @media (max-width: 1200px) {
    padding: 4px 6px !important;
    font-size: 11px !important;
  }
`;

const CompactTextField = styled(TextField)`
  .MuiInputBase-input {
    padding: 8px 14px;
    font-size: 13px;
    height: 22px;
  }
  
  .MuiInputLabel-root {
    font-size: 13px;
  }
  
  .MuiOutlinedInput-root {
    min-height: 36px;
  }
  
  @media (min-width: 1920px) {
    .MuiInputBase-input {
      padding: 10px 16px;
      font-size: 14px;
      height: 24px;
    }
    
    .MuiInputLabel-root {
      font-size: 14px;
    }
    
    .MuiOutlinedInput-root {
      min-height: 40px;
    }
  }
  
  @media (max-width: 1440px) {
    .MuiInputBase-input {
      padding: 6px 12px;
      font-size: 12px;
      height: 20px;
    }
    
    .MuiInputLabel-root {
      font-size: 12px;
    }
    
    .MuiOutlinedInput-root {
      min-height: 32px;
    }
  }
`;

const CompactAutocomplete = styled(Autocomplete)`
  .MuiInputBase-input {
    padding: 8px 14px !important;
    font-size: 13px !important;
    height: 22px !important;
  }
  
  .MuiInputLabel-root {
    font-size: 13px !important;
  }
  
  .MuiOutlinedInput-root {
    min-height: 36px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  
  .MuiAutocomplete-inputRoot {
    padding: 0 !important;
  }
  
  .MuiAutocomplete-input {
    padding: 8px 14px !important;
  }
  
  .MuiChip-root {
    height: 20px;
    font-size: 11px;
    margin: 1px;
  }
  
  .MuiAutocomplete-tag {
    margin: 1px;
  }
  
  @media (min-width: 1920px) {
    .MuiInputBase-input {
      padding: 10px 16px !important;
      font-size: 14px !important;
      height: 24px !important;
    }
    
    .MuiInputLabel-root {
      font-size: 14px !important;
    }
    
    .MuiOutlinedInput-root {
      min-height: 40px !important;
    }
    
    .MuiAutocomplete-input {
      padding: 10px 16px !important;
    }
    
    .MuiChip-root {
      height: 22px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 1440px) {
    .MuiInputBase-input {
      padding: 6px 12px !important;
      font-size: 12px !important;
      height: 20px !important;
    }
    
    .MuiInputLabel-root {
      font-size: 12px !important;
    }
    
    .MuiOutlinedInput-root {
      min-height: 32px !important;
    }
    
    .MuiAutocomplete-input {
      padding: 6px 12px !important;
    }
    
    .MuiChip-root {
      height: 18px;
      font-size: 10px;
    }
  }
`;

const ResponsiveAddIcon = styled(AddIcon)`
  @media (min-width: 1920px) {
    font-size: 60px !important;
  }
  
  @media (max-width: 1440px) {
    font-size: 40px !important;
  }
  
  @media (max-width: 1200px) {
    font-size: 35px !important;
  }
`;

// Helper Functions
const hasCommonElement = (A, B) => {
  if (Array.isArray(B)) {
    return A.some(element => B.includes(element));
  } else if (typeof B === 'string') {
    return A.includes(B);
  } else {
    return false;
  }
};

// Table Pagination Actions Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Main Component
export default function CustomPaginationActionsTable() {
  // State Management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [editCategory, setEditCategory] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [addOffTypePage, setAddOffTypePage] = useState(false);
  const [searchData, setSearchData] = useState({
    name: "",
    type: [],
    level: [],
    superioroffice: [],
    unitHead: "",
    bo: ""
  });

  // API Queries with refetchOnFocus
  const { offices } = useGetofficesQuery("OffList", {
    selectFromResult: ({ data }) => ({
      offices: data?.entities
    }),
    refetchOnFocus: true,
  });

  const { officetype } = useGetofficetypeQuery("officeTypeList", {
    selectFromResult: ({ data }) => ({
      officetype: data?.entities
    }),
    refetchOnFocus: true,
  });

  const { designations } = useGetdesignationsQuery("designations", {
    selectFromResult: ({ data }) => ({
      designations: data?.entities
    }),
  });

  const { users } = useGetUsersQuery("UserList", {
    selectFromResult: ({ data }) => ({
      users: data?.entities
    }),
  });

  // API Mutations
  const [updateOffices] = useUpdateofficeMutation();
  const [deleteOffice] = useDeleteofficeMutation();

  // Derive tabledata from offices with search filters applied
  const tabledata = useMemo(() => {
    if (!offices) return [];
    
    let tempData = Object.values(offices);

    // Apply search filters
    for (let key in searchData) {
      if (typeof searchData[key] === "string" && searchData[key].length) {
        if (["unitHead", "bo"].includes(key)) {
          tempData = tempData.filter(x => 
            users && x[key] && nameStringFn(users[x[key]]) !== "undefined" 
              ? nameStringFn(users[x[key]]).toLowerCase().includes(searchData[key].toLowerCase()) 
              : false
          );
        } else {
          tempData = tempData.filter(x => 
            x[key]?.toLowerCase().includes(searchData[key].toLowerCase())
          );
        }
      }
      
      if (typeof searchData[key] === "object" && searchData[key].length) {
        if (typeof searchData[key][0] === "string") {
          tempData = tempData.filter(x => hasCommonElement(searchData[key], x[key]));
        }
        if (typeof searchData[key][0] === "number") {
          tempData = tempData.filter(x => searchData[key]?.includes(x[key]));
        }
      }
    }
    
    // Sort by level
    return tempData.sort((a, b) => {
      let numA = parseInt(a.level.match(/\d+/)?.[0] || 0);
      let numB = parseInt(b.level.match(/\d+/)?.[0] || 0);
      return numA - numB;
    });
  }, [offices, searchData, users]);

  // Derive downloadData from tabledata
  const downloadData = useMemo(() => {
    if (!users || !tabledata.length) return [];
    
    let finalDownloadData = [];
    let finalSortedDownloadData = [];
    const keyOrderArray = ["name", "type", "level", "superioroffice", "unitHead", "bo"];
    
    for (let val of tabledata) {
      let { _id, id, ...others } = val;
      others = {
        ...others,
        name: val.name,
        type: val.type,
        level: val.level,
        superioroffice: val.superioroffice,
        unitHead: val?.unitHead?.length ? nameStringFn(users[val.unitHead]) : "Not Available",
        bo: val?.Bo?.length ? nameStringFn(users[val.Bo]) : "Not Available"
      };
      finalDownloadData.push(others);
    }
    
    for (let value of finalDownloadData) {
      let tempObject = {};
      for (let val of keyOrderArray) {
        tempObject = { ...tempObject, [val]: value[val] };
      }
      finalSortedDownloadData.push(tempObject);
    }
    
    return finalSortedDownloadData;
  }, [tabledata, users]);

  // Initialize editFormData when tabledata changes
  useEffect(() => {
    if (tabledata.length) {
      let temp = {};
      for (let i = 0; i < tabledata.length; i++) {
        temp = {
          ...temp,
          [`name${i}`]: tabledata[i].name,
          [`type${i}`]: tabledata[i].type,
          [`level${i}`]: tabledata[i].level,
          [`superioroffice${i}`]: tabledata[i].superioroffice || "Not Applicable"
        };
      }
      setEditFormData(temp);
    }
  }, [tabledata]);

  // Calculated Values
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tabledata.length) : 0;

  // Event Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onClickEdit = (e, k, row) => {
    setEditCategory(k + 1);
  };

  const onClickAddOffTypePage = () => {
    setAddOffTypePage(true);
  };

  const onChangeEditOffice = (event, newValue, name) => {
    let temp = { ...editFormData };
    if (event.target.name?.length) {
      temp = { ...temp, [event.target.name]: event.target.value };
    }
    if (name?.length) {
      temp = { ...temp, [name]: newValue };
    }
    setEditFormData(temp);
  };

  const handleSearchChange = (event, newValue, name) => {
    let tempSearchData = { ...searchData };

    if (event?.target?.name) {
      tempSearchData[event.target.name] = event.target.value;
    } else {
      tempSearchData[name] = newValue;
    }
    
    setSearchData(tempSearchData);
    // tabledata will automatically recalculate via useMemo
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditCategory(null);
      const index = ((page) * (rowsPerPage)) + (editCategory - 1);
      const id = tabledata[index].id;

      if (
        editFormData[`name${index}`]?.length &&
        editFormData[`type${index}`] &&
        editFormData[`superioroffice${index}`]
      ) {
        let body = {
          id: id,
          name: editFormData[`name${index}`],
          type: editFormData[`type${index}`],
          level: Object.values(officetype)?.find(x => x.type === editFormData[`type${index}`])?.level,
          superioroffice: editFormData[`superioroffice${index}`],
          unitHead: offices[id].unitHead || "",
          bo: offices[id].Bo || ""
        };
        
        const response = await updateOffices(body);
        console.log(response);
        // RTK Query will automatically refetch due to tag invalidation
      }
    } catch (err) {
      console.error('Error updating office:', err);
    }
  };

  const handleCancelClick = (e, k) => {
    setEditCategory(null);
  };

  const handleDeleteClick = async (e, k, id) => {
    e.preventDefault();
    const tempOfficeName = offices[id]?.name;
    const result = await deleteOffice({ id });
    if (result.data?.message?.length) {
      alert(`Office ${tempOfficeName} deleted.`);
    }
    // RTK Query will automatically refetch due to tag invalidation
  };  

  return (
    <>
      {!addOffTypePage && (
        <Displaydiv1>
          <ResponsiveHeadingWrap>
            <ResponsiveSpan>Offices</ResponsiveSpan>
          </ResponsiveHeadingWrap>

          <AddOfficeWrapper>
            <AddOffSubWrapper onClick={onClickAddOffTypePage}>
              <ResponsiveAddIcon sx={{ color: "#008080", fontSize: "50px" }} />
              <ResponsiveCommonButton>Add New Office/Offices</ResponsiveCommonButton>
            </AddOffSubWrapper>
            <ExcelDownload data={downloadData} filename={"offices"} sheetName={"sheet1"} />
          </AddOfficeWrapper>

          <form onSubmit={handleEditFormSubmit}>
            <ResponsiveTableContainer component={Paper} sx={{ backgroundColor: TableContStyle.backgroundColor }}>
              <Table aria-label="custom pagination table" size="small">
                <TableHead sx={{ backgroundColor: TableHeadStyle.backgroundColor }}>
                  <TableRow>
                    <CompactTableCell 
                      style={{ 
                        fontSize: '13px', 
                        fontWeight: TableHeadStyle.fontWeight,
                        '@media (min-width: 1920px)': { fontSize: '15px' },
                        '@media (max-width: 1440px)': { fontSize: '12px' },
                        '@media (max-width: 1200px)': { fontSize: '11px' }
                      }} 
                      sx={{ 
                        width: "4%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }}
                    >
                      Sl.No.
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "16%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      Office
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "14%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      Office Type
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "10%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      Level
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "18%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      Superior Office
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "14%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      Unit Head
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "14%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      BO
                    </CompactTableCell>
                    <CompactTableCell 
                      style={{ 
                        fontWeight: TableHeadStyle.fontWeight
                      }} 
                      sx={{ 
                        width: "10%",
                        fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '15px' }
                      }} 
                      align="left"
                    >
                      Edit
                    </CompactTableCell>
                  </TableRow>

                  {/* Search Row */}
                  <TableRow>
                    <CompactTableCell></CompactTableCell>
                    <CompactTableCell>
                      <CompactTextField 
                        sx={{ background: "white", width: "100%" }} 
                        name='name' 
                        onChange={handleSearchChange} 
                        label="Search Office" 
                        size="small" 
                        variant="outlined" 
                      />
                    </CompactTableCell>
                    <CompactTableCell>
                      <CompactAutocomplete
                        multiple
                        id="office-type-search"
                        onChange={(event, newValue) => handleSearchChange(event, newValue, "type")}
                        size="small"
                        value={searchData.type}
                        options={(officetype && offices) ? 
                          Object.values(offices)?.map(x => x.type)
                            ?.filter((item, index) => Object.values(offices)?.map(x => x.type).indexOf(item) === index)
                            .sort((a, b) => {
                              const difference = parseInt(Object.values(officetype)?.find(x => x.type === a)?.level.match(/\d+/)[0]) - 
                                parseInt(Object.values(officetype)?.find(x => x.type === b)?.level.match(/\d+/)[0]);
                              return difference;
                            }) : []
                        }
                        disableCloseOnSelect
                        getOptionLabel={(option) => String(option)}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} style={{ fontSize: '11px', padding: '4px 8px' }}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 4, marginLeft: -8, transform: 'scale(0.8)' }}
                              checked={selected}
                            />
                            {option}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Office Type" sx={{ background: "white", width: "100%" }} />
                        )}
                      />
                    </CompactTableCell>
                    <CompactTableCell>
                      <CompactAutocomplete
                        multiple
                        id="level-search"
                        onChange={(event, newValue) => handleSearchChange(event, newValue, "level")}
                        size="small"
                        value={searchData.level}
                        options={officetype ? 
                          Object.values(officetype)?.map(x => x.level)
                            .filter((item, index) => Object.values(officetype)?.map(x => x.level).indexOf(item) === index)
                            .sort((a, b) => {
                              let numA = parseInt(a.match(/\d+/)[0]);
                              let numB = parseInt(b.match(/\d+/)[0]);
                              return numA - numB;
                            }) : []
                        }
                        disableCloseOnSelect
                        getOptionLabel={(option) => String(option)}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} style={{ fontSize: '11px', padding: '4px 8px' }}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 4, marginLeft: -8, transform: 'scale(0.8)' }}
                              checked={selected}
                            />
                            {option}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Level" sx={{ background: "white", width: "100%" }} />
                        )}
                      />
                    </CompactTableCell>
                    <CompactTableCell>
                      <CompactAutocomplete
                        multiple
                        id="superior-office-search"
                        onChange={(event, newValue) => handleSearchChange(event, newValue, "superioroffice")}
                        size="small"
                        value={searchData.superioroffice}
                        options={(offices) ? Object.values(offices)?.map(x => x.name).concat(["Not Applicable"]) : []}
                        disableCloseOnSelect
                        getOptionLabel={(option) => String(option)}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} style={{ fontSize: '11px', padding: '4px 8px' }}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 4, marginLeft: -8, transform: 'scale(0.8)' }}
                              checked={selected}
                            />
                            {option}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Superior Office" sx={{ background: "white", width: "100%" }} />
                        )}
                      />
                    </CompactTableCell>
                    <CompactTableCell>
                      <CompactTextField 
                        sx={{ background: "white", width: "100%" }} 
                        name='unitHead' 
                        onChange={handleSearchChange} 
                        label="Unit Head" 
                        size="small" 
                        variant="outlined" 
                      />
                    </CompactTableCell>
                    <CompactTableCell>
                      <CompactTextField 
                        sx={{ background: "white", width: "100%" }} 
                        name='bo' 
                        onChange={handleSearchChange} 
                        label="BO" 
                        size="small" 
                        variant="outlined" 
                      />
                    </CompactTableCell>
                    <CompactTableCell></CompactTableCell>
                  </TableRow>
                </TableHead>

                {(users && offices) && (
                  <TableBody>
                    {(rowsPerPage > 0
                      ? tabledata.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : tabledata
                    ).map((row, key) => (
                      <Fragment key={key * 2}>
                        {editCategory === key + 1 ? (
                          <OffEditableRow 
                            row={row} 
                            k={key} 
                            editFormData={editFormData} 
                            handleCancelClick={handleCancelClick} 
                            page={page} 
                            rowsPerPage={rowsPerPage} 
                            editCategory={editCategory} 
                            offices={offices} 
                            onChangeEditOffice={onChangeEditOffice} 
                            officetype={officetype} 
                            designations={designations} 
                            tableFontSize={tableFontSize} 
                            SubmitButton={SubmitButton}
                            CompactTableCell={CompactTableCell}
                          />
                        ) : (
                          <OffReadOnlyRow 
                            row={row} 
                            k={key} 
                            tabledata={tabledata} 
                            onClickEdit={onClickEdit}
                            handleDeleteClick={handleDeleteClick}
                            page={page} 
                            rowsPerPage={rowsPerPage} 
                            editCategory={editCategory}
                            tableFontSize={tableFontSize} 
                            SubmitButton={SubmitButton} 
                            users={users}
                            CompactTableCell={CompactTableCell}
                          />
                        )}
                      </Fragment>
                    ))}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 40 * emptyRows }}>
                        <CompactTableCell colSpan={8} />
                      </TableRow>
                    )}
                  </TableBody>
                )}
                
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      colSpan={8}
                      count={tabledata.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: {
                          'aria-label': 'rows per page',
                        },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                      sx={{
                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                          fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '14px' }
                        },
                        '.MuiTablePagination-select': {
                          fontSize: { xs: '11px', sm: '12px', md: '13px', lg: '13px', xl: '14px' }
                        },
                        '@media (min-width: 1920px)': {
                          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                            fontSize: '14px'
                          },
                          '.MuiTablePagination-select': {
                            fontSize: '14px'
                          }
                        }
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </ResponsiveTableContainer>
          </form>
        </Displaydiv1>
      )}

      {addOffTypePage && (
        <AddOffice 
          setAddOffTypePage={setAddOffTypePage} 
          officetype={officetype} 
          offices={offices} 
        />
      )}
    </>
  );
}